import React from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  styled,
} from '@mui/material';
import { Email, Phone, Facebook, Instagram, Twitter, YouTube, Send } from '@mui/icons-material';

const ContactCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '24px',
  padding: '40px',
  margin: '0 auto 0 auto',
  maxWidth: '800px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '60px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  position: 'relative',
  top: '50px',
  zIndex: 2,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: '30px',
    padding: '30px',
  },
}));

const ContactItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const ContactIcon = styled(Box)({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
});

const FooterContainer = styled(Box)({
  backgroundColor: '#2d3748',
  color: 'white',
  paddingTop: '100px',
  paddingBottom: '40px',
});

const FooterContent = styled(Container)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: '60px',
  marginBottom: '60px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: '40px',
    textAlign: 'center',
  },
}));

const SocialIcons = styled(Box)({
  display: 'flex',
  gap: '20px',
  marginTop: '24px',
});

const SocialIcon = styled(IconButton)({
  color: 'white',
  '&:hover': {
    color: '#6366f1',
  },
});

const NewsletterForm = styled(Box)({
  display: 'flex',
  marginTop: '24px',
  gap: '0',
});

const NewsletterInput = styled(TextField)({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    borderRadius: '8px 0 0 8px',
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    padding: '16px',
    fontSize: '16px',
  },
});

const NewsletterButton = styled(Button)({
  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  borderRadius: '0 8px 8px 0',
  minWidth: '60px',
  height: '56px',
  '&:hover': {
    background: 'linear-gradient(135deg, #5856eb 0%, #7c3aed 100%)',
  },
});

const QuickLink = styled(Typography)({
  color: '#a0aec0',
  marginBottom: '12px',
  cursor: 'pointer',
  '&:hover': {
    color: 'white',
  },
});

const CopyrightSection = styled(Box)({
  borderTop: '1px solid #4a5568',
  paddingTop: '30px',
  textAlign: 'center',
});

const Footer = () => {
  return (
    <Box>
      <ContactCard>
        <ContactItem>
          <ContactIcon>
            <Email />
          </ContactIcon>
          <Typography variant="h6" color="text.primary" fontWeight={500}>
            info@homeslygroup.com
          </Typography>
        </ContactItem>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: '60px',
            backgroundColor: '#e2e8f0',
            display: { xs: 'none', md: 'block' },
          }}
        />

        <ContactItem>
          <ContactIcon>
            <Phone />
          </ContactIcon>
          <Typography variant="h6" color="text.primary" fontWeight={500}>
            02045259955
          </Typography>
        </ContactItem>
      </ContactCard>

      <FooterContainer>
        <FooterContent maxWidth="lg">
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Homesly
            </Typography>
            <Typography
              variant="body1"
              color="#a0aec0"
              sx={{
                mb: 3,
                lineHeight: 1.6,
                textAlign: 'justify',
              }}
            >
              Homesly is designed to simplify your everyday living with a modern, seamless, and
              user-friendly experience. From smart features to elegant design, we help users manage
              everything effortlessly in one place. Our platform focuses on convenience,
              reliability, and innovation to deliver the best digital experience for every user.
            </Typography>
            <SocialIcons>
              <SocialIcon>
                <Facebook />
              </SocialIcon>
              <SocialIcon>
                <Instagram />
              </SocialIcon>
              <SocialIcon>
                <Twitter />
              </SocialIcon>
              <SocialIcon>
                <YouTube />
              </SocialIcon>
            </SocialIcons>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              QUICK LINK
            </Typography>
            <Box sx={{ mt: 3 }}>
              <QuickLink
                variant="body1"
                onClick={() =>
                  document.getElementById('about').scrollIntoView({ behavior: 'smooth' })
                }
              >
                About
              </QuickLink>
              <QuickLink
                variant="body1"
                onClick={() =>
                  document.getElementById('features').scrollIntoView({ behavior: 'smooth' })
                }
              >
                Features
              </QuickLink>
              <QuickLink
                variant="body1"
                onClick={() =>
                  document.getElementById('interface').scrollIntoView({ behavior: 'smooth' })
                }
              >
                Screenshot
              </QuickLink>
              <QuickLink
                variant="body1"
                onClick={() =>
                  document.getElementById('review').scrollIntoView({ behavior: 'smooth' })
                }
              >
                Blog
              </QuickLink>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              NEWS LETTER
            </Typography>
            <Typography variant="body1" color="#a0aec0" sx={{ mb: 3, lineHeight: 1.6 }}>
              Subscribe our newsletter to get our latest update & news
            </Typography>
            <NewsletterForm>
              <NewsletterInput placeholder="Your email address" variant="outlined" fullWidth />
              <NewsletterButton variant="contained">
                <Send />
              </NewsletterButton>
            </NewsletterForm>
          </Box>
        </FooterContent>

        <CopyrightSection>
          <Typography variant="body2" color="#a0aec0">
            © 2025 Homesly. Developed by{' '}
            <a
              href="https://ytctechventures.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#a0aec0', textDecoration: 'underline' }}
            >
              YTC TechVentures
            </a>
            . All Rights Reserved.
          </Typography>
        </CopyrightSection>
      </FooterContainer>
    </Box>
  );
};

export default Footer;
